import { useEffect, useState, useMemo, Fragment } from "react";
import { useQuickEditRefresh } from "@/hooks/useQuickEditRefresh";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { safeFormatDate } from "@/lib/dates";
import { stripLegacyLabel } from "@/lib/legacy-label";
import { PageHeader, PoolPill } from "@/components/ui-extra";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Trophy, RefreshCw, Loader2, Download, Pencil, RotateCcw,
  ChevronDown, ChevronRight, ChevronUp, Tag, MapPin, MessageSquareText, FileDown, Share2,
} from "lucide-react";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/currency";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { ShareHorseDialog } from "@/components/ShareHorseDialog";
// pdfReport is dynamically imported inside the export handler so jsPDF
// stays out of the initial bundle. This keeps first-paint fast and
// dramatically shortens the webpack compile that runs at pod boot.

export default function RankingsPage() {
  const { user, hasPerm } = useAuth();
  const isMaster = !!user?.is_master;
  const canRecompute = isMaster || hasPerm?.("horses:recompute_ranking");
  // Rankings-only viewers should never learn a horse's medical/injury
  // details; they see the "safe" expanded view.
  const canSeeHorseDetails = isMaster || hasPerm?.("horses:read");
  // Per-horse ranking detail PDF. Granted to every role by default but
  // revocable in the Roles editor (`rankings:download_horse_report`).
  const canDownloadReport = isMaster || hasPerm?.("rankings:download_horse_report");
  const [reportingId, setReportingId] = useState(null);
  const [shareHorse, setShareHorse] = useState(null);
  const [horses, setHorses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  const [editing, setEditing] = useState(null); // horse being edited
  const [editForm, setEditForm] = useState({ score: "", chukkas_count: "" });
  const [savingEdit, setSavingEdit] = useState(false);
  const [clearingId, setClearingId] = useState(null);
  // Expandable row state — only one horse expanded at a time. Toggling
  // the same row collapses it. Clicking a different row switches.
  const [expandedId, setExpandedId] = useState(null);
  // Per-horse writeup cache. Rankings-only users can still see the
  // rider comments, so we lazily fetch them when a row expands.
  const [notesById, setNotesById] = useState({});
  const [notesLoadingId, setNotesLoadingId] = useState(null);
  // Per-horse photo cache. Loaded on-demand alongside the writeups when
  // the row is expanded so the initial leaderboard render stays lean.
  const [photosById, setPhotosById] = useState({});
  const [photosLoadingId, setPhotosLoadingId] = useState(null);
  // Whether ANY horse in the current list has a `listed_price` — used
  // to decide whether to render the Price column header at all so the
  // table stays lean when no one has pushed a price yet.
  const anyListed = useMemo(
    () => horses.some((h) => Number.isFinite(h.listed_price)),
    [horses],
  );

  const toggleExpand = (h) => {
    setExpandedId((prev) => (prev === h.id ? null : h.id));
    // Kick off a lazy fetch of writeups + photos the first time this
    // horse expands. Both are cached so subsequent toggles are instant.
    if (h.id !== expandedId) {
      if (!notesById[h.id]) loadWriteups(h.id);
      if (!photosById[h.id]) loadPhotos(h.id);
    }
  };

  const loadWriteups = async (horseId) => {
    setNotesLoadingId(horseId);
    try {
      // Fetch a generous batch of chukkas so that even old campaigners
      // with hundreds of rides expose every writeup here. Backend caps
      // at whatever it returns; if we ever hit that we can page. Only
      // rows with a non-blank writeup end up on screen.
      const res = await api.get(`/horses/${horseId}/chukkas`, { params: { limit: 500 } });
      const writeups = (res.data || [])
        .filter((c) => (c.writeup || "").trim().length > 0);
      setNotesById((prev) => ({ ...prev, [horseId]: writeups }));
    } catch (_e) {
      // Non-fatal — rankings-only users may not even have access to
      // /chukkas. In that case we just show "No rider notes".
      setNotesById((prev) => ({ ...prev, [horseId]: [] }));
    } finally {
      setNotesLoadingId(null);
    }
  };

  const loadPhotos = async (horseId) => {
    setPhotosLoadingId(horseId);
    try {
      const res = await api.get(`/horses/${horseId}/photos`);
      const photos = res.data?.photos || [];
      setPhotosById((prev) => ({ ...prev, [horseId]: photos }));
    } catch (_e) {
      setPhotosById((prev) => ({ ...prev, [horseId]: [] }));
    } finally {
      setPhotosLoadingId(null);
    }
  };

  const downloadHorseReport = async (h, rank) => {
    if (reportingId) return;
    setReportingId(h.id);
    try {
      const [chukkasRes, photosRes] = await Promise.all([
        // Full rating history (not just writeups) so the report can
        // average every metric. Tolerate roles that can't read it.
        api.get(`/horses/${h.id}/chukkas`, { params: { limit: 500 } }).catch(() => ({ data: [] })),
        photosById[h.id]
          ? Promise.resolve({ data: { photos: photosById[h.id] } })
          : api.get(`/horses/${h.id}/photos`).catch(() => ({ data: { photos: [] } })),
      ]);
      const { downloadHorseReport: build } = await import("@/lib/pdfHorseReport");
      await build({
        horse: h,
        rank,
        total: horses.length,
        chukkas: chukkasRes.data || [],
        photos: photosRes.data?.photos || [],
        canSeeHorseDetails,
      });
      toast.success(`Report ready for ${h.name}`);
    } catch (e) {
      toast.error("Could not generate the horse report");
      console.error(e);
    } finally {
      setReportingId(null);
    }
  };

  const openEditor = (h) => {
    setEditing(h);
    setEditForm({
      score: h?.ranking?.score != null ? String(h.ranking.score) : "",
      chukkas_count: h?.ranking?.chukkas_count != null ? String(h.ranking.chukkas_count) : "",
    });
  };

  const saveOverride = async () => {
    const s = parseFloat(editForm.score);
    const c = parseInt(editForm.chukkas_count, 10);
    if (Number.isNaN(s) || s < 0 || s > 100) { toast.error("Score must be 0-100"); return; }
    if (Number.isNaN(c) || c < 0) { toast.error("Sessions must be ≥ 0"); return; }
    setSavingEdit(true);
    try {
      const res = await api.put(`/horses/${editing.id}/ranking-override`, {
        score: s,
        chukkas_count: c,
      });
      toast.success(`Override set for ${editing.name}`);
      setHorses((prev) => prev.map((h) => (h.id === editing.id ? { ...h, ranking: res.data.ranking, ranking_override: res.data.override } : h)));
      setEditing(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save override");
    } finally {
      setSavingEdit(false);
    }
  };

  const clearOverride = async (h) => {
    setClearingId(h.id);
    try {
      const res = await api.delete(`/horses/${h.id}/ranking-override`);
      toast.success(`Override removed from ${h.name}`);
      setHorses((prev) => prev.map((x) => (x.id === h.id ? { ...x, ranking: res.data.ranking, ranking_override: null } : x)));
    } catch (e) {
      toast.error("Could not clear override");
      throw e;
    } finally {
      setClearingId(null);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/horses/rankings");
      setHorses(res.data);
    } finally { setLoading(false); }
  };
  // Silent refetch used for polling / focus refresh — does not toggle
  // the loading skeleton so viewers reading the table don't see a
  // flicker every minute.
  const silentReload = async () => {
    try {
      const res = await api.get("/horses/rankings");
      setHorses(res.data);
    } catch (_e) { /* transient network hiccups shouldn't yell */ }
  };
  useEffect(() => { load(); }, []);
  useQuickEditRefresh(load);
  // Real-time-ish refresh so that when an admin pushes new prices from
  // another tab/session, this leaderboard picks them up without the
  // viewer needing to manually reload:
  //   • Refetch every 60s while the tab is visible.
  //   • Refetch immediately on window focus / tab return.
  //   • Also listen to a custom "horses:prices-published" event so a
  //     push done in the SAME browser propagates instantly.
  useEffect(() => {
    let interval;
    const onVisible = () => {
      if (document.visibilityState === "visible") silentReload();
    };
    const onFocus = () => silentReload();
    const onPricesPushed = () => silentReload();
    if (document.visibilityState === "visible") {
      interval = setInterval(silentReload, 60_000);
    }
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    window.addEventListener("horses:prices-published", onPricesPushed);
    return () => {
      if (interval) clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("horses:prices-published", onPricesPushed);
    };
  }, []);

  const recomputeAll = async () => {
    setRecomputing(true);
    try {
      const res = await api.post("/horses/recompute-all");
      toast.success(`Recomputed ${res.data.updated} horses`);
      load();
    } catch (e) { toast.error("Failed"); }
    finally { setRecomputing(false); }
  };

  const exportPdf = async () => {
    try {
      const rows = horses.map((h, i) => ({
        rank: i + 1,
        name: h.name,
        pool: h?.ranking?.pool || "Unrated",
        score: h?.ranking?.score != null ? Number(h.ranking.score).toFixed(1) : "",
        chukkas: h?.ranking?.chukkas_count ?? "",
        age: h.age ?? "",
        location: h.location || "",
      }));
      const { downloadReport } = await import("@/lib/pdfReport");
      await downloadReport({
        eyebrow: "OPERATIONS",
        title: "Horse Rankings",
        period: "Ranked by Formula v9 score, ordered highest to lowest",
        filename: "rankings",
        summary: [
          { label: "Ranked", value: horses.length },
          { label: "Elite", value: poolCounts.Elite },
          { label: "Good", value: poolCounts.Good },
        ],
        columns: [
          { header: "#", dataKey: "rank", mono: true, halign: "right" },
          { header: "Horse", dataKey: "name" },
          { header: "Pool", dataKey: "pool" },
          { header: "Score", dataKey: "score", mono: true, halign: "right" },
          { header: "Sessions", dataKey: "chukkas", mono: true, halign: "right" },
          { header: "Age", dataKey: "age", mono: true, halign: "right" },
          { header: "Location", dataKey: "location" },
        ],
        rows,
      });
    } catch (e) {
      toast.error("Could not generate PDF");
      console.error(e);
    }
  };

  // Pool distribution counts across the ranked horses
  const poolCounts = useMemo(() => {
    const counts = { Elite: 0, Good: 0, "Average+": 0, "Below Average": 0, Unsalvageable: 0, Unrated: 0 };
    for (const h of horses) {
      const p = h?.ranking?.pool || "Unrated";
      counts[p] = (counts[p] || 0) + 1;
    }
    return counts;
    // counts/h/p are locally scoped inside the callback and re-created
    // each call — no closure risk. Only `horses` is a real dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [horses]);

  // Column count for the desktop table — shifts when master-admin
  // controls or the price column are visible.
  const colCount = 8 + (isMaster ? 1 : 0) + (anyListed ? 1 : 0) + 1; // +1 for expand chevron

  return (
    <div className="space-y-6">
      <PageHeader
        title="Leaderboard"
        subtitle="Ranked by Formula v9 score. updated after every horse ridden"
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={exportPdf}
              disabled={loading || horses.length === 0}
              data-testid="rankings-download-pdf-button"
            >
              <Download className="h-4 w-4 mr-2" /> Download PDF
            </Button>
            {canRecompute && (
              <Button variant="secondary" onClick={recomputeAll} disabled={recomputing} data-testid="rankings-recompute-button">
                {recomputing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />} Recompute all
              </Button>
            )}
          </div>
        }
      />
      <Card className="rounded-xl overflow-hidden">
        <div className="px-4 pt-4 pb-2 flex flex-wrap gap-2" data-testid="rankings-pool-distribution">
          {Object.entries(poolCounts).map(([name, n]) => {
            if (n === 0) return null;
            return (
              <span
                key={name}
                className="inline-flex items-center gap-2 rounded-full border border-border bg-muted/30 px-3 py-1 text-xs"
                data-testid={`rankings-pool-pill-${name}`}
              >
                <span className="font-medium">{name}</span>
                <span className="rounded-full bg-background/60 px-1.5 text-[10px] font-semibold">{n}</span>
              </span>
            );
          })}
        </div>
        <div className="hidden md:block overflow-auto" data-testid="horse-leaderboard-table">
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--ap-paper))]">
              <tr className="text-xs uppercase tracking-[0.14em] text-muted-foreground border-b">
                <th className="text-left px-3 py-3 w-10"></th>
                <th className="text-left px-4 py-3 w-16">#</th>
                <th className="text-left px-4 py-3">Horse</th>
                <th className="text-left px-4 py-3">Pool</th>
                <th className="text-right px-4 py-3">Score</th>
                <th className="text-right px-4 py-3">Performance</th>
                <th className="text-right px-4 py-3">Consistency</th>
                <th className="text-right px-4 py-3">Durability</th>
                <th className="text-right px-4 py-3">Sessions</th>
                {anyListed && (
                  <th className="text-right px-4 py-3">Price</th>
                )}
                {isMaster && <th className="text-right px-4 py-3 w-24"></th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={colCount} className="text-center py-10 text-muted-foreground">Loading…</td></tr>
              ) : horses.length === 0 ? (
                <tr><td colSpan={colCount} className="text-center py-12 text-muted-foreground">No horses yet.</td></tr>
              ) : horses.map((h, i) => {
                const isExpanded = expandedId === h.id;
                const forSale = h.status === "for_sale";
                return (
                  <Fragment key={h.id}>
                    <tr
                      className="border-b hover:bg-accent/60 group cursor-pointer"
                      data-testid="horse-leaderboard-row"
                      onClick={() => toggleExpand(h)}
                    >
                      <td className="px-3 py-3 text-muted-foreground">
                        {isExpanded
                          ? <ChevronDown className="h-4 w-4" />
                          : <ChevronRight className="h-4 w-4" />}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-muted-foreground">{i + 1}</span>
                          {i === 0 && <Trophy className="h-3.5 w-3.5 text-[hsl(var(--ap-brass))]" />}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {canSeeHorseDetails ? (
                            <Link
                              to={`/horses/${h.id}`}
                              className="font-medium hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {h.name}
                            </Link>
                          ) : (
                            <span className="font-medium">{h.name}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3"><PoolPill pool={h.ranking?.pool} stars={h.ranking?.stars} /></td>
                      <td className="px-4 py-3 text-right font-serif text-lg tabular-nums">{(h.ranking?.score || 0).toFixed(1)}</td>
                      <td className="px-4 py-3 text-right font-mono text-xs text-muted-foreground">{(h.ranking?.performance || 0).toFixed(0)}</td>
                      <td className="px-4 py-3 text-right font-mono text-xs text-muted-foreground">{(h.ranking?.consistency || 0).toFixed(0)}</td>
                      <td className="px-4 py-3 text-right font-mono text-xs text-muted-foreground">{(h.ranking?.durability || 0).toFixed(0)}</td>
                      <td className="px-4 py-3 text-right text-muted-foreground">{h.ranking?.chukkas_count ?? 0}</td>
                      {anyListed && (
                        <td className="px-4 py-3 text-right" data-testid={`horse-price-${h.id}`}>
                          <PriceCell price={h.listed_price} forSale={forSale} />
                        </td>
                      )}
                      {isMaster && (
                        <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="inline-flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditor(h)}
                              data-testid={`override-edit-${h.id}`}
                              aria-label={`Edit score and sessions for ${h.name}`}
                              title="Edit score and sessions"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            {h.ranking?.override_active && (
                              <ConfirmDialog
                                title={`Clear ranking override for ${h.name}?`}
                                description={
                                  <>
                                    <p>The master-admin baseline score and session count you set for <span className="font-medium text-foreground">{h.name}</span> will be removed. The score will be recomputed from actual ride ratings only.</p>
                                    <p className="mt-2 text-xs">You can re-apply an override at any time from the same edit dialog. The horse&rsquo;s existing ride history is untouched.</p>
                                  </>
                                }
                                confirmLabel="Yes, clear override"
                                destructive={false}
                                onConfirm={() => clearOverride(h)}
                                testId={`override-clear-${h.id}`}
                              >
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  disabled={clearingId === h.id}
                                  data-testid={`override-clear-${h.id}`}
                                  aria-label={`Clear override for ${h.name}`}
                                  title="Clear override and recompute from sessions"
                                  className="text-muted-foreground hover:text-destructive"
                                >
                                  {clearingId === h.id
                                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    : <RotateCcw className="h-3.5 w-3.5" />}
                                </Button>
                              </ConfirmDialog>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                    {isExpanded && (
                      <tr
                        className="border-b bg-[hsl(var(--ap-paper))]/60"
                        data-testid={`horse-expanded-row-${h.id}`}
                      >
                        <td colSpan={colCount} className="px-6 py-4">
                          <ExpandedHorsePanel
                            horse={h}
                            canSeeHorseDetails={canSeeHorseDetails}
                            writeups={notesById[h.id]}
                            writeupsLoading={notesLoadingId === h.id}
                            photos={photosById[h.id]}
                            photosLoading={photosLoadingId === h.id}
                            canDownloadReport={canDownloadReport}
                            downloading={reportingId === h.id}
                            onDownloadReport={() => downloadHorseReport(h, i + 1)}
                            onShare={() => setShareHorse(h)}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile leaderboard cards */}
        <div className="md:hidden divide-y" data-testid="horse-leaderboard-cards">
          {loading ? (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">Loading…</div>
          ) : horses.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-muted-foreground">No horses yet.</div>
          ) : horses.map((h, i) => {
            const isExpanded = expandedId === h.id;
            const forSale = h.status === "for_sale";
            return (
              <div key={h.id} data-testid="horse-leaderboard-card">
                <button
                  type="button"
                  onClick={() => toggleExpand(h)}
                  className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-accent/60 focus-visible:bg-accent/60 focus-visible:outline-none"
                  aria-expanded={isExpanded}
                  data-testid={`horse-card-toggle-${h.id}`}
                >
                  <div className="text-muted-foreground shrink-0">
                    {isExpanded
                      ? <ChevronDown className="h-4 w-4" />
                      : <ChevronRight className="h-4 w-4" />}
                  </div>
                  <div className="w-8 text-center shrink-0">
                    <div className="font-mono text-sm text-muted-foreground">{i + 1}</div>
                    {i === 0 && <Trophy className="h-3.5 w-3.5 text-[hsl(var(--ap-brass))] mx-auto mt-0.5" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {canSeeHorseDetails ? (
                        <Link
                          to={`/horses/${h.id}`}
                          className="font-medium hover:underline truncate"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {h.name}
                        </Link>
                      ) : (
                        <span className="font-medium truncate">{h.name}</span>
                      )}
                      <PoolPill pool={h.ranking?.pool} stars={h.ranking?.stars} />
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                      <span>P {(h.ranking?.performance || 0).toFixed(0)}</span>
                      <span>C {(h.ranking?.consistency || 0).toFixed(0)}</span>
                      <span>D {(h.ranking?.durability || 0).toFixed(0)}</span>
                      <span>{h.ranking?.chukkas_count ?? 0} sessions</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0 flex items-center gap-2">
                    <div>
                      <div className="font-serif text-xl tabular-nums leading-none">{(h.ranking?.score || 0).toFixed(1)}</div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">score</div>
                    </div>
                    {isMaster && (
                      <div className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openEditor(h)}
                          data-testid={`override-edit-mobile-${h.id}`}
                          aria-label={`Edit score for ${h.name}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        {h.ranking?.override_active && (
                          <ConfirmDialog
                            title={`Clear ranking override for ${h.name}?`}
                            description={
                              <>
                                <p>The master-admin baseline score and session count you set for <span className="font-medium text-foreground">{h.name}</span> will be removed. The score will be recomputed from actual ride ratings only.</p>
                                <p className="mt-2 text-xs">You can re-apply an override at any time. The horse&rsquo;s ride history is untouched.</p>
                              </>
                            }
                            confirmLabel="Yes, clear override"
                            destructive={false}
                            onConfirm={() => clearOverride(h)}
                            testId={`override-clear-mobile-${h.id}`}
                          >
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-destructive"
                              disabled={clearingId === h.id}
                              data-testid={`override-clear-mobile-${h.id}`}
                              aria-label={`Clear override for ${h.name}`}
                            >
                              {clearingId === h.id
                                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                : <RotateCcw className="h-3.5 w-3.5" />}
                            </Button>
                          </ConfirmDialog>
                        )}
                      </div>
                    )}
                  </div>
                </button>
                {isExpanded && (
                  <div className="px-4 pb-4 pt-1 bg-[hsl(var(--ap-paper))]/60" data-testid={`horse-expanded-card-${h.id}`}>
                    <ExpandedHorsePanel
                      horse={h}
                      canSeeHorseDetails={canSeeHorseDetails}
                      writeups={notesById[h.id]}
                      writeupsLoading={notesLoadingId === h.id}
                      photos={photosById[h.id]}
                      photosLoading={photosLoadingId === h.id}
                      canDownloadReport={canDownloadReport}
                      downloading={reportingId === h.id}
                      onDownloadReport={() => downloadHorseReport(h, i + 1)}
                      onShare={() => setShareHorse(h)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>
      {canDownloadReport && (
        <ShareHorseDialog
          horse={shareHorse}
          open={!!shareHorse}
          onOpenChange={(v) => !v && setShareHorse(null)}
        />
      )}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="override-dialog">
          <DialogHeader>
            <DialogTitle>Edit ranking for {editing?.name}</DialogTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Set a baseline score (0-100) and session count. Any new ride ratings will be
              blended on top of these values, weighted by their count. so the math of new
              ratings adds on top of the seeded values.
            </p>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <div>
              <Label>Score (0-100)</Label>
              <Input
                type="number" step="0.1" min="0" max="100"
                value={editForm.score}
                onChange={(e) => setEditForm({ ...editForm, score: e.target.value })}
                data-testid="override-score-input"
              />
            </div>
            <div>
              <Label>Sessions</Label>
              <Input
                type="number" min="0" step="1"
                value={editForm.chukkas_count}
                onChange={(e) => setEditForm({ ...editForm, chukkas_count: e.target.value })}
                data-testid="override-sessions-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={savingEdit}>Cancel</Button>
            <Button onClick={saveOverride} disabled={savingEdit} data-testid="override-save-button">
              {savingEdit ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save baseline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Compact "sale status + price" cell used in the leaderboard price
 * column. Renders a subtle badge for "Not for sale" so it never
 * dominates the row, and a stronger, brass-tinted badge for "For sale"
 * so it stands out to executives scanning the sheet.
 */
function PriceCell({ price, forSale }) {
  const hasPrice = Number.isFinite(price);
  return (
    <div className="inline-flex flex-col items-end gap-0.5">
      <span className="font-mono tabular-nums text-sm">
        {hasPrice ? formatCurrency(price, "USD") : <span className="text-muted-foreground">—</span>}
      </span>
      {forSale ? (
        <span className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--ap-brass)/0.4)] bg-[hsl(var(--ap-brass)/0.10)] text-[hsl(var(--ap-saddle))] px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider">
          <Tag className="h-2.5 w-2.5" strokeWidth={2} /> For sale
        </span>
      ) : (
        <span className="text-[9px] uppercase tracking-wider text-muted-foreground">Not for sale</span>
      )}
    </div>
  );
}

/**
 * Panel rendered inside an expanded leaderboard row.
 *
 * Two "trust levels":
 *   1. `canSeeHorseDetails = true`  → shows the operational context an
 *      admin needs at a glance: location, arrival, class, work status.
 *   2. `canSeeHorseDetails = false` (viewer role, `rankings:read` only)
 *      → shows only public-safe stats + rider notes. Injury status,
 *      severity, vet comments and internal notes are intentionally
 *      omitted so an external stakeholder browsing the leaderboard can't
 *      infer sensitive medical detail.
 *
 * Rider writeups from the /rate WhatsApp flow are shown to EVERYONE,
 * because they're the qualitative story behind the score and were
 * explicitly requested to remain visible.
 */
function ExpandedHorsePanel({
  horse, canSeeHorseDetails, writeups, writeupsLoading, photos, photosLoading,
  canDownloadReport, downloading, onDownloadReport, onShare,
}) {
  const h = horse || {};
  const r = h.ranking || {};
  const forSale = h.status === "for_sale";
  // Only render the price sub-panel if the operator has actually
  // PUSHED a price for this horse (populated `listed_price`). We
  // deliberately never show `purchase_price` on the leaderboard —
  // that's the internal figure and stays behind the horse inventory
  // page.
  const hasListed = Number.isFinite(h.listed_price);
  return (
    <div className="grid gap-4 md:grid-cols-12" data-testid={`horse-expanded-panel-${h.id}`}>
      {/* Photo strip — horizontal, scrollable/swipeable. Shows every
          photo the horse has (newest first). Empty state hides the
          strip entirely so unphotographed horses don't waste vertical
          space. Snap-x makes swiping on touch devices feel natural. */}
      <HorsePhotoStrip
        horseId={h.id}
        horseName={h.name}
        photos={photos}
        photosLoading={photosLoading}
      />

      {/* Stats grid — public-safe metrics only. */}
      <div className="md:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 text-sm">
        <Stat label="Performance" value={(r.performance || 0).toFixed(0)} mono />
        <Stat label="Consistency" value={(r.consistency || 0).toFixed(0)} mono />
        <Stat label="Durability" value={(r.durability || 0).toFixed(0)} mono />
        <Stat label="Sessions" value={r.chukkas_count ?? 0} mono />
        {h.age != null && <Stat label="Age" value={`${h.age}y`} mono />}
        {h.breed && <Stat label="Breed" value={h.breed} />}
        {canSeeHorseDetails && h.location && (
          <Stat label="Location" value={<span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {h.location}</span>} />
        )}
        {canSeeHorseDetails && h.computed_class && <Stat label="Class" value={h.computed_class} />}
        {canSeeHorseDetails && h.work_status && <Stat label="Work status" value={h.work_status} />}
        {canSeeHorseDetails && h.time_at_farm_days != null && (
          <Stat label="Time at farm" value={`${h.time_at_farm_days}d`} mono />
        )}
      </div>

      {/* Right rail: price panel visible to EVERYONE when a listed
          price has been pushed by the operator. */}
      <div className="md:col-span-5 space-y-3">
        {hasListed && (
          <div
            className="rounded-lg border border-border/70 bg-background/60 p-3"
            data-testid={`horse-expanded-price-${h.id}`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium">Asking price</div>
              {forSale ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--ap-brass)/0.4)] bg-[hsl(var(--ap-brass)/0.12)] text-[hsl(var(--ap-saddle))] px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider">
                  <Tag className="h-3 w-3" strokeWidth={2} /> For sale
                </span>
              ) : (
                <span className="inline-flex items-center rounded-full border border-border bg-muted/40 text-muted-foreground px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider">
                  Not for sale
                </span>
              )}
            </div>
            <div className="mt-1 font-serif text-2xl tabular-nums" data-testid={`horse-expanded-price-value-${h.id}`}>
              {formatCurrency(h.listed_price, "USD")}
            </div>
          </div>
        )}

        {/* Rider notes — always visible per product decision. Lazily
            loaded when the row expands; graceful empty state if the
            user's role can't fetch them. Extracted into its own
            component so the "Show all N notes" toggle can hold
            expand-state without leaking into the parent. */}
        <RiderNotesSection
          horseId={h.id}
          writeups={writeups}
          writeupsLoading={writeupsLoading}
        />

        <div className="flex items-center justify-between gap-3 flex-wrap">
          {canSeeHorseDetails ? (
            <Link
              to={`/horses/${h.id}`}
              className="inline-flex items-center gap-1 text-xs text-[hsl(var(--ap-forest))] hover:underline"
              data-testid={`horse-expanded-detail-link-${h.id}`}
            >
              Open full profile →
            </Link>
          ) : <span />}
          {canDownloadReport && (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => { e.stopPropagation(); onDownloadReport?.(); }}
              disabled={downloading}
              data-testid={`horse-report-download-${h.id}`}
              aria-label={`Download ranking report for ${h.name}`}
            >
              {downloading
                ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                : <FileDown className="h-3.5 w-3.5 mr-1.5" />}
              Download report
            </Button>
          )}
          {canDownloadReport && (
            <Button
              variant="ghost"
              size="sm"
              className="text-[hsl(var(--ap-forest))] hover:text-[hsl(var(--ap-forest))]"
              onClick={(e) => { e.stopPropagation(); onShare?.(); }}
              data-testid={`horse-report-share-${h.id}`}
              aria-label={`Share ranking report for ${h.name}`}
            >
              <Share2 className="h-3.5 w-3.5 mr-1.5" />
              Share link
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{label}</div>
      <div className={mono ? "font-mono tabular-nums" : ""}>{value}</div>
    </div>
  );
}

/**
 * Horizontal, swipeable strip of horse photos for the expanded row.
 *
 * Behaviour:
 *   • Newest photo first, scroll horizontally to browse older shots.
 *   • CSS scroll-snap so swipes feel deliberate on touch.
 *   • Uses the `card` (480px) variant for a balance of quality vs weight.
 *   • Lazy: only rendered when the parent panel is visible AND the
 *     photo list has loaded.
 *   • No-photo state: renders nothing (keeps the panel compact for
 *     unphotographed horses).
 *   • Loading: shows skeleton tiles so the layout doesn't jump when
 *     the network is slow.
 *   • Photo endpoints are open (no auth) so this works for viewer-role
 *     users who only have `rankings:read`.
 */
function HorsePhotoStrip({ horseId, horseName, photos, photosLoading }) {
  // Skeleton row while photos are still being fetched. Keeps the
  // expanded panel a consistent height on first open.
  if (photosLoading && !photos) {
    return (
      <div className="md:col-span-12" data-testid={`horse-expanded-photos-loading-${horseId}`}>
        <div className="flex gap-2 overflow-hidden">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-32 w-32 shrink-0 rounded-lg bg-muted animate-pulse"
              aria-hidden="true"
            />
          ))}
        </div>
      </div>
    );
  }
  if (!photos || photos.length === 0) return null;
  return (
    <div className="md:col-span-12" data-testid={`horse-expanded-photos-${horseId}`}>
      <div
        className="flex gap-2 overflow-x-auto snap-x snap-mandatory pb-1 -mx-1 px-1 scroll-smooth"
        role="list"
        aria-label={`Photos of ${horseName || "horse"}`}
      >
        {photos.map((p, idx) => {
          // Prefer the versioned per-photo URL (survives future updates)
          // and fall back to the "current" photo endpoint for legacy
          // photos that only exist under the pre-history layout.
          const src =
            p.photo_id && !String(p.photo_id).startsWith("legacy-")
              ? `/api/horses/${horseId}/photos/${p.photo_id}/card`
              : `/api/horses/${horseId}/photo/card`;
          return (
            <div
              key={p.photo_id || idx}
              role="listitem"
              className="relative h-32 w-32 sm:h-36 sm:w-36 shrink-0 snap-start overflow-hidden rounded-lg border border-border/70 bg-muted"
              data-testid={`horse-expanded-photo-${horseId}-${idx}`}
            >
              <img
                src={src}
                alt={`${horseName || "Horse"} photo ${idx + 1}`}
                loading="lazy"
                className="h-full w-full object-cover"
                draggable={false}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Rider-notes list for the expanded row. Shows the first
 * `COLLAPSED_COUNT` writeups by default; an inline toggle expands
 * to every writeup available for the horse.
 *
 * ── Why a dedicated subcomponent ──
 *   • Keeps the parent `ExpandedHorsePanel` pure — the expand-state
 *     lives here so re-expanding a horse doesn't lose its "showing
 *     all" state between renders of the outer table.
 *   • Isolates the (small) toggle animation without re-rendering
 *     every other section of the panel.
 *   • Makes it obvious the writeups source is always the FULL list
 *     the parent fetched via `loadWriteups` — no hidden slicing lives
 *     upstream.
 */
function RiderNotesSection({ horseId, writeups, writeupsLoading }) {
  const [showAll, setShowAll] = useState(false);
  const COLLAPSED_COUNT = 3;
  const total = writeups?.length ?? 0;
  const canCollapse = total > COLLAPSED_COUNT;
  const visible = !canCollapse || showAll ? writeups : writeups.slice(0, COLLAPSED_COUNT);
  return (
    <div className="rounded-lg border border-border/70 bg-background/60 p-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium">
          <MessageSquareText className="h-3 w-3" /> Rider notes
          {total > 0 && (
            <span
              className="text-muted-foreground normal-case tracking-normal"
              data-testid={`horse-expanded-notes-count-${horseId}`}
            >
              ({total})
            </span>
          )}
        </div>
      </div>
      {writeupsLoading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading rider notes…
        </div>
      ) : total === 0 ? (
        <p className="text-xs text-muted-foreground italic">No rider notes yet for this horse.</p>
      ) : (
        <>
          <ul className="space-y-2" data-testid={`horse-expanded-notes-${horseId}`}>
            {visible.map((c) => {
              // Suppress "Legacy import" placeholders (see
              // /lib/legacy-label.js). Empty rider falls back to
              // "Unknown rider"; empty comp-type just hides the pill.
              const rider = stripLegacyLabel(c.rider_name);
              const compType = stripLegacyLabel(c.competition_type);
              const bit = (c.bit_used || "").trim();
              return (
                <li key={c.id} className="text-xs border-l-2 border-[hsl(var(--ap-forest))] pl-2">
                  <div className="text-muted-foreground text-[10px] uppercase tracking-wider flex items-baseline justify-between gap-2 flex-wrap">
                    <span>
                      <span className="font-medium text-foreground">{rider || "Unknown rider"}</span>
                      {compType ? ` · ${compType}` : ""}
                      {bit ? (
                        <span data-testid={`horse-expanded-note-bit-${c.id}`}> · {bit} bit</span>
                      ) : null}
                    </span>
                    {c.created_at && (
                      <span
                        className="text-muted-foreground normal-case tracking-normal"
                        data-testid={`horse-expanded-note-date-${c.id}`}
                      >
                        {safeFormatDate(c.created_at, "MMM d, yyyy · HH:mm")}
                      </span>
                    )}
                  </div>
                  {/* When the operator has opted-in to see everything
                      we lift the 3-line clamp so long writeups aren't
                      re-truncated at the paragraph level. Collapsed
                      view keeps the clamp for a scannable overview. */}
                  <p
                    className={`whitespace-pre-wrap leading-snug mt-0.5 ${showAll ? "" : "line-clamp-3"}`}
                  >
                    {c.writeup}
                  </p>
                </li>
              );
            })}
          </ul>
          {canCollapse && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-[hsl(var(--ap-forest))] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ap-forest))] focus-visible:ring-offset-2 rounded"
              data-testid={`horse-expanded-notes-toggle-${horseId}`}
              aria-expanded={showAll}
            >
              {showAll ? (
                <>
                  <ChevronUp className="h-3.5 w-3.5" /> Show less
                </>
              ) : (
                <>
                  <ChevronDown className="h-3.5 w-3.5" /> Show all {total} notes
                </>
              )}
            </button>
          )}
        </>
      )}
    </div>
  );
}
